/**
 * Utilidad para hacer fetch con reintentos, backoff exponencial y circuit breaker
 */

// Circuit breaker state
const circuitBreakers = new Map();

const REQUEST_TIMEOUT = 12000; // Techo de espera por intento

// Por debajo de esto, un «Failed to fetch» NO se reintenta: ver `esInmediato`.
const FALLO_INMEDIATO = 400;

const CIRCUIT_BREAKER_THRESHOLD = 8; // Número de errores consecutivos antes de abrir el circuito
const CIRCUIT_BREAKER_TIMEOUT = 60000; // Tiempo en ms antes de intentar cerrar el circuito (1 minuto)
const CIRCUIT_BREAKER_HALF_OPEN_REQUESTS = 1; // Número de peticiones permitidas en estado "half-open"

/**
 * Estados del circuit breaker
 */
const CircuitState = {
  CLOSED: 'CLOSED',     // Funcionando normalmente
  OPEN: 'OPEN',         // Bloqueando peticiones debido a errores
  HALF_OPEN: 'HALF_OPEN' // Permitiendo algunas peticiones de prueba
};

/**
 * Obtiene o crea un circuit breaker para una URL base
 */
function getCircuitBreaker(baseUrl) {
  if (!circuitBreakers.has(baseUrl)) {
    circuitBreakers.set(baseUrl, {
      state: CircuitState.CLOSED,
      failureCount: 0,
      lastFailureTime: null,
      successCount: 0
    });
  }
  return circuitBreakers.get(baseUrl);
}

/**
 * Verifica si el circuit breaker permite hacer la petición
 */
function canMakeRequest(baseUrl) {
  const breaker = getCircuitBreaker(baseUrl);

  if (breaker.state === CircuitState.CLOSED) {
    return true;
  }

  if (breaker.state === CircuitState.OPEN) {
    // Verificar si ha pasado suficiente tiempo para intentar de nuevo
    const timeSinceLastFailure = Date.now() - breaker.lastFailureTime;
    if (timeSinceLastFailure > CIRCUIT_BREAKER_TIMEOUT) {
      breaker.state = CircuitState.HALF_OPEN;
      breaker.successCount = 0;
      console.log(`🔄 Circuit breaker para ${baseUrl} cambió a HALF_OPEN`);
      return true;
    }
    return false;
  }

  if (breaker.state === CircuitState.HALF_OPEN) {
    // Permitir algunas peticiones de prueba
    return breaker.successCount < CIRCUIT_BREAKER_HALF_OPEN_REQUESTS;
  }

  return false;
}

/**
 * Registra un éxito en el circuit breaker
 */
function recordSuccess(baseUrl) {
  const breaker = getCircuitBreaker(baseUrl);

  if (breaker.state === CircuitState.HALF_OPEN) {
    breaker.successCount++;
    if (breaker.successCount >= CIRCUIT_BREAKER_HALF_OPEN_REQUESTS) {
      breaker.state = CircuitState.CLOSED;
      breaker.failureCount = 0;
      console.log(`✅ Circuit breaker para ${baseUrl} cambió a CLOSED (restaurado)`);
    }
  } else if (breaker.state === CircuitState.CLOSED) {
    breaker.failureCount = 0;
  }
}

/**
 * Registra un fallo en el circuit breaker
 */
function recordFailure(baseUrl) {
  const breaker = getCircuitBreaker(baseUrl);
  breaker.failureCount++;
  breaker.lastFailureTime = Date.now();

  if (breaker.state === CircuitState.HALF_OPEN) {
    breaker.state = CircuitState.OPEN;
    console.warn(`⚠️ Circuit breaker para ${baseUrl} volvió a OPEN después de fallo en HALF_OPEN`);
  } else if (breaker.failureCount >= CIRCUIT_BREAKER_THRESHOLD) {
    breaker.state = CircuitState.OPEN;
    console.warn(`🚫 Circuit breaker para ${baseUrl} cambió a OPEN después de ${breaker.failureCount} fallos consecutivos`);
  }
}

/**
 * Extrae la URL base de una URL completa
 */
function getBaseUrl(url) {
  try {
    const urlObj = new URL(url);
    return `${urlObj.protocol}//${urlObj.host}`;
  } catch {
    return url;
  }
}

/**
 * Calcula el tiempo de espera para el reintento usando backoff exponencial
 */
function calculateBackoff(attemptNumber, baseDelay = 1000) {
  // Backoff exponencial con jitter
  const exponentialDelay = Math.min(baseDelay * Math.pow(2, attemptNumber - 1), 10000);
  const jitter = Math.random() * 1000; // Añadir hasta 1 segundo de jitter
  return exponentialDelay + jitter;
}

/**
 * Determina si un error es recuperable y vale la pena reintentar
 */
function isRetriableError(error) {
  // Errores de red que vale la pena reintentar
  const retriableErrors = [
    'Failed to fetch',
    'NetworkError',
    'Network request failed',
    'ERR_SOCKET_NOT_CONNECTED',
    'ERR_CONNECTION_RESET',
    'ERR_NETWORK',
    'ECONNRESET',
    'ETIMEDOUT'
  ];

  const errorMessage = error.message || error.toString();
  return retriableErrors.some(retriable => errorMessage.includes(retriable));
}

/**
 * Hace una petición fetch con reintentos automáticos y circuit breaker
 *
 * @param {string} url - URL a la que hacer la petición
 * @param {object} options - Opciones de fetch
 * @param {number} maxRetries - Número máximo de reintentos (default: 3)
 * @param {number} baseDelay - Delay base en ms para backoff exponencial (default: 1000)
 * @returns {Promise<Response>} Respuesta de fetch
 */
export async function fetchWithRetry(url, options = {}, maxRetries = 3, baseDelay = 1000) {
  const baseUrl = getBaseUrl(url);

  // Verificar circuit breaker
  if (!canMakeRequest(baseUrl)) {
    const error = new Error(`Circuit breaker OPEN para ${baseUrl}. Evitando petición.`);
    error.circuitBreakerOpen = true;
    throw error;
  }

  let lastError;
  let timeoutId;

  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    const inicioIntento = Date.now();
    try {
      // Treinta segundos era la espera de antes, y con tres reintentos encima
      // dejaba una vista en blanco durante minuto y medio antes de rendirse.
      // Nada de lo que pide esta web —el tema que suena, la parrilla, unos
      // posts— vale una espera así: si en doce segundos no ha contestado, se
      // sigue sin ello.
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      const fetchOptions = {
        ...options,
        signal: controller.signal
      };

      const response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);

      // Si la respuesta es exitosa, registrar éxito en circuit breaker
      if (response.ok) {
        recordSuccess(baseUrl);
        return response;
      }

      // Si es un error 4xx (error del cliente), no reintentar ni penalizar el circuit breaker
      // Un 404 es una respuesta válida del servidor, no un fallo de infraestructura
      if (response.status >= 400 && response.status < 500) {
        throw new Error(`HTTP Error ${response.status}: ${response.statusText}`);
      }

      // Para errores 5xx, reintentar
      lastError = new Error(`HTTP Error ${response.status}: ${response.statusText}`);

    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      lastError = error;

      // Si es un error de abort por timeout, no reintentar
      if (error.name === 'AbortError') {
        console.warn(`⏱️ Timeout (${REQUEST_TIMEOUT / 1000}s) en petición a ${url} - no se reintentará`);
        recordFailure(baseUrl);
        throw error; // No reintentar timeouts
      }

      // Si no es un error recuperable, lanzar inmediatamente
      if (!isRetriableError(error)) {
        recordFailure(baseUrl);
        throw error;
      }

      // 🔴 Un «Failed to fetch» instantáneo NO es una red congestionada.
      //
      // Desde JavaScript, un bloqueo por CORS, un fallo de DNS y un contenido
      // mixto dan todos el mismo `TypeError: Failed to fetch` que da una red
      // caída, y por eso estaba en la lista de recuperables. Lo que los separa
      // es CUÁNTO tardan en fallar: la red tarda; un veto del navegador es
      // inmediato —medido el 26.08 contra la API de la radio: 70 ms—.
      //
      // Esperar y repetir algo que el navegador va a vetar igual solo suma
      // segundos de página en blanco. Se falla a la primera y la vista se
      // entera enseguida.
      if (Date.now() - inicioIntento < FALLO_INMEDIATO) {
        console.warn(`⛔ ${url} falló al instante (${Date.now() - inicioIntento}ms): no se reintenta — no es la red, es un veto del navegador.`);
        recordFailure(baseUrl);
        throw error;
      }
    }

    // Si no es el último intento, esperar antes de reintentar
    if (attempt <= maxRetries) {
      const delay = calculateBackoff(attempt, baseDelay);
      console.log(`🔄 Reintentando petición a ${url} en ${Math.round(delay)}ms (intento ${attempt + 1}/${maxRetries + 1})`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // Si llegamos aquí, todos los reintentos fallaron
  recordFailure(baseUrl);
  console.error(`❌ Todos los reintentos fallaron para ${url}:`, lastError);
  throw lastError;
}

/**
 * Wrapper para hacer peticiones JSON con reintentos
 */
export async function fetchJSONWithRetry(url, options = {}, maxRetries = 3) {
  try {
    const response = await fetchWithRetry(url, options, maxRetries);
    return await response.json();
  } catch (error) {
    // Si el circuit breaker está abierto, lanzar error específico
    if (error.circuitBreakerOpen) {
      console.warn(`⚠️ Circuit breaker abierto, no se realizó la petición a ${url}`);
    }
    throw error;
  }
}

/**
 * Resetea manualmente un circuit breaker (útil para debugging)
 */
export function resetCircuitBreaker(baseUrl) {
  if (circuitBreakers.has(baseUrl)) {
    circuitBreakers.get(baseUrl).state = CircuitState.CLOSED;
    circuitBreakers.get(baseUrl).failureCount = 0;
    console.log(`🔧 Circuit breaker para ${baseUrl} reseteado manualmente`);
  }
}

/**
 * Obtiene el estado de todos los circuit breakers (útil para debugging)
 */
export function getCircuitBreakerStatus() {
  const status = {};
  circuitBreakers.forEach((breaker, url) => {
    status[url] = {
      state: breaker.state,
      failureCount: breaker.failureCount,
      lastFailureTime: breaker.lastFailureTime ? new Date(breaker.lastFailureTime).toISOString() : null
    };
  });
  return status;
}
